import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { CostRecord, CreateCostRecord, UpdateCostRecord, CostSummary } from '../types';

const fetchCostRecords = async (contractId: string): Promise<CostRecord[]> => {
  return api.get<CostRecord[]>(`/contracts/${contractId}/costs`);
};

const fetchCostSummary = async (contractId: string): Promise<CostSummary> => {
  return api.get<CostSummary>(`/contracts/${contractId}/costs/summary`);
};

const createCostRecord = async ({ contractId, data }: { contractId: string; data: CreateCostRecord }): Promise<CostRecord> => {
  return api.post<CostRecord>(`/contracts/${contractId}/costs`, data);
};

const updateCostRecord = async ({ contractId, id, data }: { contractId: string; id: string; data: UpdateCostRecord }): Promise<CostRecord> => {
  return api.put<CostRecord>(`/contracts/${contractId}/costs/${id}`, data);
};

const deleteCostRecord = async ({ contractId, id }: { contractId: string; id: string }): Promise<void> => {
  return api.delete<void>(`/contracts/${contractId}/costs/${id}`);
};

export const useCostRecords = (contractId: string) => {
  return useQuery({
    queryKey: queryKeys.costRecords.byContract(contractId),
    queryFn: () => fetchCostRecords(contractId),
    enabled: !!contractId,
  });
};

export const useCostSummary = (contractId: string) => {
  return useQuery({
    queryKey: queryKeys.costRecords.summary(contractId),
    queryFn: () => fetchCostSummary(contractId),
    enabled: !!contractId,
  });
};

export const useCreateCostRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCostRecord,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.summary(variables.contractId) });
      // The budget report's Actual column is computed from cost records
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

export const useUpdateCostRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCostRecord,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.summary(variables.contractId) });
      // The budget report's Actual column is computed from cost records
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};

export const useDeleteCostRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCostRecord,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.byContract(variables.contractId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.summary(variables.contractId) });
      // The budget report's Actual column is computed from cost records
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
};
